// Hooks de dados do piloto automático (fatia S3b).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AutopilotPolicyDto, AutopilotRunDto, AutopilotStatusDto, SimulateBody, SimulationResultDto, UpdateAutopilotPolicyBody } from '@impulso/shared/api/autopilot'
import { api } from '../../lib/api'

const base = (companyId: string) => `/companies/${companyId}/autopilot`

export function useAutopilotPolicy(companyId: string) {
  return useQuery({ queryKey: ['autopilot', companyId, 'policy'], queryFn: () => api.get<AutopilotPolicyDto>(base(companyId)), enabled: !!companyId })
}

export function useAutopilotStatus(companyId: string) {
  return useQuery({ queryKey: ['autopilot', companyId, 'status'], queryFn: () => api.get<AutopilotStatusDto>(`${base(companyId)}/status`), enabled: !!companyId })
}

export function useAutopilotRuns(companyId: string, limit = 15) {
  return useQuery({ queryKey: ['autopilot', companyId, 'runs', limit], queryFn: () => api.get<{ items: AutopilotRunDto[] }>(`${base(companyId)}/runs?limit=${limit}`), enabled: !!companyId })
}

function useInvalidate(companyId: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['autopilot', companyId] })
    void qc.invalidateQueries({ queryKey: ['recommendations', companyId] })
    void qc.invalidateQueries({ queryKey: ['actions', companyId] })
  }
}

export function useUpdatePolicy(companyId: string) {
  const qc = useQueryClient()
  const inv = useInvalidate(companyId)
  return useMutation({
    mutationFn: (body: UpdateAutopilotPolicyBody) => api.put<AutopilotPolicyDto>(base(companyId), body),
    onSuccess: (data) => { qc.setQueryData(['autopilot', companyId, 'policy'], data); inv() },
  })
}

export function usePauseResume(companyId: string) {
  const qc = useQueryClient()
  const inv = useInvalidate(companyId)
  return useMutation({
    mutationFn: (v: { pause: boolean; reason?: string }) => api.post<AutopilotPolicyDto>(`${base(companyId)}/${v.pause ? 'pause' : 'resume'}`, v.pause ? { reason: v.reason ?? null } : {}),
    onSuccess: (data) => { qc.setQueryData(['autopilot', companyId, 'policy'], data); inv() },
  })
}

export function useRunNow(companyId: string) {
  const inv = useInvalidate(companyId)
  return useMutation({
    mutationFn: () => api.post<{ runId: string; proposals: number; executed: number; recommended: number; blocked: number; notes: string[] }>(`${base(companyId)}/run`),
    onSuccess: inv,
  })
}

export function useSimulate(companyId: string) {
  return useMutation({ mutationFn: (body: SimulateBody) => api.post<SimulationResultDto>(`${base(companyId)}/simulate`, body) })
}
